import { Button, Modal, Select } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConvertPlaceholderMember } from 'src/Hooks/Mutations/useConvertPlaceholderMember';
import { AssetMemberDto } from 'src/Services/API/generated';

import styles from './MergePlaceholderMemberModal.module.scss';
import { Props } from './MergePlaceholderMemberModal.types';

const memberName = (member: AssetMemberDto): string =>
  [member.user.firstName, member.user.lastName].filter(Boolean).join(' ') || member.user.email;

const MergePlaceholderMemberModal = ({ open, onClose, placeholder, candidates }: Props) => {
  const { t } = useTranslation();
  const { mutate: convert, isLoading } = useConvertPlaceholderMember();
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);

  const handleClose = () => {
    setTargetUserId(null);
    setTargetError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!placeholder) return;
    if (!targetUserId) {
      // Never a silent disabled button: the reason shows under the field.
      setTargetError(
        t('admin.placeholderMembers.merge.targetRequired', 'Choisissez le compte à garder.')
      );
      return;
    }
    // A placeholder row carries the placeholder id in `user.id` (see useFetchMembers).
    convert({ placeholderId: placeholder.user.id, targetUserId }, { onSuccess: handleClose });
  };

  const placeholderName = placeholder ? memberName(placeholder) : '';

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      title={t('admin.placeholderMembers.merge.title', 'Fusionner avec un compte')}
      size="md"
    >
      <div className={styles.container}>
        <p className={styles.description}>
          {t(
            'admin.placeholderMembers.merge.description',
            '{{name}} a déjà un compte sur ce bien ? Choisissez-le : ses parts de dépenses, ses versements et ses lignes d’appel de fonds passeront sur ce compte, et {{name}} n’apparaîtra plus qu’une fois dans les soldes.',
            { name: placeholderName }
          )}
        </p>
        <Select
          label={t('admin.placeholderMembers.merge.target', 'Compte de {{name}}', {
            name: placeholderName,
          })}
          placeholder={t('admin.placeholderMembers.merge.targetPlaceholder', 'Choisir un membre')}
          data={candidates.map(candidate => ({
            value: candidate.user.id,
            label: memberName(candidate),
          }))}
          value={targetUserId}
          onChange={value => {
            setTargetUserId(value);
            setTargetError(null);
          }}
          error={targetError}
          searchable
          comboboxProps={{ withinPortal: true }}
        />
        <p className={styles.warning}>
          {t(
            'admin.placeholderMembers.merge.warning',
            'La fusion est définitive : le membre non inscrit disparaît de la liste.'
          )}
        </p>
        <div className={styles.buttons}>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            {t('admin.placeholderMembers.merge.submit', 'Fusionner')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MergePlaceholderMemberModal;
